<script>
import ResultDisplay from "@homepage/components/GenericResultDisplay.vue";
import Countdown from "@homepage/components/Countdown.vue";
import {generateSimpleImageMapFor} from "@homepage/helpers/image";
import PrizesList from "@homepage/components/PrizesList.vue";
import LotteryResult from "@homepage/components/LotteryResult.vue";
import LotteryResultAutoDisplay from "@homepage/components/LotteryResultAutoDisplay.vue";
import CardPresenter from "@homepage/components/CardPresenter.vue";

export default {
	name: 'EasyMoneyBoard',
	props: {
		generalInformation: {
			type: Object,
			required: true
		}
	},
	components: {
		CardPresenter, LotteryResultAutoDisplay, LotteryResult,
		PrizesList,
		ResultDisplay,
		Countdown
	},
	provide() {
		return {
			imagesMap: generateSimpleImageMapFor('easymoney'),
			generalInformation: this.generalInformation
		};
	},
	data() {
		return {
			timeToShowPrizesPage: 15000,
			timeToShowEachLotteryResultPage: 5000,
			lotteryEntriesPerPage: 2,
			currentShowingPage: null
		};
	},
	computed: {
		timeToShowResultsPage() {
			const timeToShowEachPage = this.timeToShowEachLotteryResultPage / 1000;

			return Math.ceil(this.generalInformation.lotteriesResultsEntries.length / this.lotteryEntriesPerPage) * timeToShowEachPage * 1000;
		}
	},
	beforeCreate() {
		this.$emit('change-style', {backgroundImage: require('@/assets/img/easymoney/background.jpg')});
	}
}
</script>

<template>
	<v-row class="px-3" justify="center">
		<v-col
			cols="4"
			class="light-green--bg white--text rounded-bl-xl"
		>
			<v-row justify="center" no-gutters>
				<v-col cols="auto">
					<v-img id="logo" height="135px" contain
								 :src="require('@/assets/img/easymoney/logo.png')">
					</v-img>
				</v-col>
			</v-row>
			<v-row no-gutters>
				<countdown
					id="upcoming-result"
					class="text-center mt-5"
					:sortition="generalInformation.sortition">
					<template #default='{sortition}'>
						<v-flex md3>
							<h1 class='font-weight-bold text-uppercase'>Next draw in</h1>
						</v-flex>
						<v-flex md3>
							<h2 id='next-sortition' class='font-weight-bold'>{{ sortition.name }}</h2>
						</v-flex>
						<v-flex md3>
									<span id='time-left' v-if='sortition.timeLeft'
												class='font-weight-black text-uppercase'>{{ sortition.timeLeft }}</span>
							<span v-else><font-awesome-icon icon='sync' spin size='3x'></font-awesome-icon></span>
						</v-flex>
					</template>
				</countdown>
			</v-row>
			<v-row no-gutters>
				<v-col cols="12">
					<div class="text-center"><span>Winning Numbers</span></div>
					<result-display
						v-for="result in generalInformation.results.slice(0, 1)"
						:key="result.sortition.id"
						:digits-image-dimensions="{height: '35px', width: '35px'}"
						:pick-three-image="{src: require('@/assets/img/easymoney/pickThree_w.png'), width: '100%', height: '34px',contain: true}"
						:pick-four-image="{src: require('@/assets/img/easymoney/pickFour_w.png'), width: '100%', height: '34px',contain: true}"
						:result="result"
					>
					</result-display>
				</v-col>
			</v-row>
		</v-col>
		<v-col cols="8">
			<v-row id="header-row">
				<v-col cols="6" class="d-flex white--text align-center">
					<v-row class="pick-numbers" no-gutters>
						<v-col class="text-center"><span class="headers"> PICKS</span></v-col>
						<v-col class="text-center"><span class="headers">NUMBERS</span></v-col>
					</v-row>
				</v-col>
				<v-col cols="6" class="d-flex align-center justify-center white--text rounded-tr-xl">
					<span class="headers">
						{{ currentShowingPage === 0 ? 'PAYOUTS' : 'RESULTS' }}
					</span>
				</v-col>
			</v-row>
			<v-row>
				<v-col
					cols="6"
					class="white--bg main--text"
				>
					<result-display
						v-for="result in generalInformation.results.slice(1,3)"
						:key="result.sortition.id"
						:result="result"
						class="pb-0"
						:digits-image-dimensions="{height: '35px', width: '35px'}"
						:pick-three-image="{src: require('@/assets/img/easymoney/pickThree_g.png'), width: '100%', height: '34px',contain: true}"
						:pick-four-image="{src: require('@/assets/img/easymoney/pickFour_g.png'), width: '100%', height: '34px',contain: true}"
					>
					</result-display>
				</v-col>
				<v-col
					cols="6"
					class="gray--bg rounded-br-xl"
				>
					<card-presenter
						:time-per-page='[timeToShowPrizesPage, timeToShowResultsPage]'
						@page-changed="currentShowingPage = $event">
						<template #page-1>
							<prizes-list :general-information='generalInformation'>
								<template #prize-row='{prize}'>
									<v-col class='prize-row light-green--bg white--text rounded-lg' cols='12'>
										<v-row no-gutters class='py-1'>
											<v-col class='prize-name text-center' cols='6'>
												{{ prize.name }} - 1X:
											</v-col>
											<v-col class='prize-amount text-center' cols='6'>
												{{ prize.amount }}
											</v-col>
										</v-row>
									</v-col>
								</template>
							</prizes-list>
						</template>
						<template #page-2>
							<v-card id='lottery-results' class='secondary-card' shaped>
								<lottery-result-auto-display
									:general-information='generalInformation'
									:entries-per-page='lotteryEntriesPerPage'
									:time-to-show-each-page='timeToShowEachLotteryResultPage'>
									<template #page='{ entries }'>
										<v-container class='px-0 py-0'>
											<lottery-result v-for='(entry, index) in entries'
																			class='lottery-result'
																			:class="{'rounded-b-xl': index === entries.length - 1}"
																			:display-as-one="false"
																			:show-pick-five='true'
																			:key='entry.name'
																			:lottery='entry'>
												<template #sortition-header='{sortition, icon, date}'>
															<span class='sortition-header'>
																<font-awesome-icon :icon='icon'></font-awesome-icon>
																{{ sortition.name }}: {{ date }}
															</span>
												</template>
											</lottery-result>
										</v-container>
									</template>
								</lottery-result-auto-display>
							</v-card>
						</template>
					</card-presenter>
					<div id="page-url" class="d-flex justify-center">
						<span>Visit our page easylottery.tv</span>
					</div>
				</v-col>
			</v-row>
		</v-col>
	</v-row>
</template>

<style scoped lang="scss">
@font-face {
	font-family: "RobotoBlack";
	src: url("../../../../public/fonts/roboto-black-webfont.woff");
}

$main-color: #05a551;
$blue: #0017A1;

* {
	font-family: "RobotoBlack", "Liberation Sans Narrow", serif;
}

.main--text {
	color: $main-color;
}

.light-green--bg {
	background-color: $main-color;
}

#header-row > .col {
	background: $main-color;
}

.headers {
	font-size: 1.5rem;
	font-weight: 800;
}

#next-sortition {
	font-size: 2.5rem;
	font-weight: 800;
	line-height: 35px;
	text-align: center;
}

#time-left {
	font-weight: bold;
	font-size: 2.6rem;
	text-align: center;
	color: white;
}

.prize-name {
	font-family: "RobotoBlack", serif;
	font-size: 1.7rem;
	font-weight: bold;
}

.prize-amount {
	font-size: 2rem;
	font-weight: bold;
	font-family: "DonJose", "Liberation Sans Narrow", serif;
}

#page-url {
	font-size: 1.5rem;
	font-weight: 800;
}

::v-deep .numbers-divider {
	background-color: $main-color;
	width: 30px;
	margin: 0 8px;
}

::v-deep .sortition-name {
	font-size: 1.7rem;
	font-weight: bold;
}
</style>
